import React from 'react';
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Line, Doughnut } from 'react-chartjs-2';
import { useSuperDuper } from '../context/SuperDuperContext';
import { renderLoading, renderError } from '../components/StatusViews';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  ChartDataLabels
);

const AnalyticsTab = () => {
  const {
    analyticsData,
    analyticsLoading,
    analyticsError,
    analyticsDays,
    setAnalyticsDays,
    fetchAnalytics
  } = useSuperDuper();

  return (
    <>
      <Typography
        variant="h4"
        sx={{ mb: { xs: 2, sm: 4 }, fontSize: { xs: '1.35rem', sm: '2.125rem' } }}
      >
        Page Views Analytics
      </Typography>
      
      {analyticsLoading ? (
        renderLoading()
      ) : analyticsError ? (
        renderError(analyticsError)
      ) : analyticsData ? (
        <Grid container spacing={3}>
          {/* Summary Cards */}
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Total Views
                    </Typography>
                    <Typography variant="h4" component="div">
                      {analyticsData.isHourly 
                        ? (analyticsData.hourly?.summary?.totalViews || 0)
                        : (analyticsData.daily?.summary?.totalViews || 0)
                      }
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Unique Visitors
                    </Typography>
                    <Typography variant="h4" component="div">
                      {analyticsData.isHourly 
                        ? (analyticsData.hourly?.summary?.totalUniqueViews || 0)
                        : (analyticsData.daily?.summary?.totalUniqueViews || 0)
                      }
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Home Page Views
                    </Typography>
                    <Typography variant="h4" component="div">
                      {analyticsData.isHourly 
                        ? (analyticsData.hourly?.summary?.totalHomeViews || 0)
                        : (analyticsData.daily?.summary?.totalHomeViews || 0)
                      }
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Pricing Page Views
                    </Typography>
                    <Typography variant="h4" component="div">
                      {analyticsData.isHourly 
                        ? (analyticsData.hourly?.summary?.totalPricingViews || 0)
                        : (analyticsData.daily?.summary?.totalPricingViews || 0)
                      }
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>

          {/* Days Filter */}
          <Grid item xs={12}>
            <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Button
                variant={analyticsDays === 1 ? "contained" : "outlined"}
                onClick={() => {
                  setAnalyticsDays(1);
                  fetchAnalytics(1);
                }}
                size="small"
              >
                Last 24 Hours
              </Button>
              <Button
                variant={analyticsDays === 7 ? "contained" : "outlined"}
                onClick={() => {
                  setAnalyticsDays(7);
                  fetchAnalytics(7);
                }}
                size="small"
              >
                Last 7 Days
              </Button>
              <Button
                variant={analyticsDays === 30 ? "contained" : "outlined"}
                onClick={() => {
                  setAnalyticsDays(30);
                  fetchAnalytics(30);
                }}
                size="small"
              >
                Last 30 Days
              </Button>
            </Box>
          </Grid>

          {/* Line Chart - Daily/Hourly Views */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {analyticsData.isHourly ? 'Hourly Page Views (Last 24 Hours)' : `Daily Page Views (Last ${analyticsDays} days)`}
              </Typography>
              {analyticsData.isHourly && analyticsData.hourly?.hourlyData && (
                <Line
                  data={{
                    labels: analyticsData.hourly.hourlyData.map(d => d.hourLabel),
                    datasets: [
                      {
                        label: 'Total Views',
                        data: analyticsData.hourly.hourlyData.map(d => d.totalViews),
                        borderColor: '#FF6B35',
                        backgroundColor: 'rgba(255, 107, 53, 0.1)',
                        tension: 0.4,
                        datalabels: {
                          display: true,
                          align: 'top',
                          anchor: 'end',
                          color: '#FF6B35',
                          font: {
                            weight: 'bold',
                            size: 11
                          },
                          formatter: (value) => value
                        }
                      },
                      {
                        label: 'Home Page',
                        data: analyticsData.hourly.hourlyData.map(d => d.homeViews),
                        borderColor: '#06AED5',
                        backgroundColor: 'rgba(6, 174, 213, 0.1)',
                        tension: 0.4,
                        datalabels: {
                          display: false
                        }
                      },
                      {
                        label: 'Pricing Page',
                        data: analyticsData.hourly.hourlyData.map(d => d.pricingViews),
                        borderColor: '#4CAF50',
                        backgroundColor: 'rgba(76, 175, 80, 0.1)',
                        tension: 0.4,
                        datalabels: {
                          display: false
                        }
                      }
                    ]
                  }}
                  options={{
                    responsive: true,
                    plugins: {
                      legend: {
                        position: 'top',
                      },
                      title: {
                        display: false,
                      },
                      datalabels: {
                        display: false
                      }
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                      },
                    },
                  }}
                />
              )}
              {!analyticsData.isHourly && analyticsData.daily?.dailyData && (
                <Line
                  data={{
                    labels: analyticsData.daily.dailyData.map(d => 
                      new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                    ),
                    datasets: [
                      {
                        label: 'Total Views',
                        data: analyticsData.daily.dailyData.map(d => d.totalViews),
                        borderColor: '#FF6B35',
                        backgroundColor: 'rgba(255, 107, 53, 0.1)',
                        tension: 0.4,
                        datalabels: {
                          display: true,
                          align: 'top',
                          anchor: 'end',
                          color: '#FF6B35',
                          font: {
                            weight: 'bold',
                            size: 11
                          },
                          formatter: (value) => value
                        }
                      },
                      {
                        label: 'Home Page',
                        data: analyticsData.daily.dailyData.map(d => d.homeViews),
                        borderColor: '#06AED5',
                        backgroundColor: 'rgba(6, 174, 213, 0.1)',
                        tension: 0.4,
                        datalabels: {
                          display: false
                        }
                      },
                      {
                        label: 'Pricing Page',
                        data: analyticsData.daily.dailyData.map(d => d.pricingViews),
                        borderColor: '#4CAF50',
                        backgroundColor: 'rgba(76, 175, 80, 0.1)',
                        tension: 0.4,
                        datalabels: {
                          display: false
                        }
                      }
                    ]
                  }}
                  options={{
                    responsive: true,
                    plugins: {
                      legend: {
                        position: 'top',
                      },
                      title: {
                        display: false,
                      },
                      datalabels: {
                        display: false
                      }
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                      },
                    },
                  }}
                />
              )}
            </Paper>
          </Grid>

          {/* Doughnut Chart - Referrers */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Traffic Sources
              </Typography>
              {analyticsData.referrers?.referrerSummary && (
                <Doughnut
                  data={{
                    labels: Object.keys(analyticsData.referrers.referrerSummary),
                    datasets: [
                      {
                        data: Object.values(analyticsData.referrers.referrerSummary).map(r => r.totalViews),
                        backgroundColor: [
                          '#FF6B35', // YoungPresso orange
                          '#06AED5', // Blue
                          '#4CAF50', // Green
                          '#FF9800', // Orange
                          '#9C27B0', // Purple
                          '#607D8B', // Blue Grey
                        ],
                        borderWidth: 2,
                        borderColor: '#fff',
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    plugins: {
                      legend: {
                        position: 'bottom',
                      },
                    },
                  }}
                />
              )}
            </Paper>
          </Grid>

          {/* Referrer Details Table */}
          <Grid item xs={12}>
            <Paper sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Referrer Details
              </Typography>
              <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                <Table sx={{ minWidth: 480 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Referrer</TableCell>
                      <TableCell align="right">Total Views</TableCell>
                      <TableCell align="right">Unique Visitors</TableCell>
                      <TableCell align="right">Percentage</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {analyticsData.referrers?.referrerSummary && 
                      Object.entries(analyticsData.referrers.referrerSummary)
                        .sort(([,a], [,b]) => b.totalViews - a.totalViews)
                        .map(([referrer, data]) => {
                          const totalViews = analyticsData.daily?.summary?.totalViews || 1;
                          const percentage = ((data.totalViews / totalViews) * 100).toFixed(1);
                          
                          return (
                            <TableRow key={referrer}>
                              <TableCell>
                                <Chip
                                  label={referrer}
                                  color={
                                    referrer === 'Instagram' ? 'secondary' :
                                    referrer === 'YoungPresso.com' ? 'primary' :
                                    referrer === 'Google' ? 'success' :
                                    'default'
                                  }
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell align="right">{data.totalViews}</TableCell>
                              <TableCell align="right">{data.uniqueViews}</TableCell>
                              <TableCell align="right">{percentage}%</TableCell>
                            </TableRow>
                          );
                        })
                    }
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          {/* Instagram vs YoungPresso.com Comparison */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Instagram Traffic
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="h4" sx={{ mr: 2 }}>
                  {analyticsData.referrers?.referrerSummary?.Instagram?.totalViews || 0}
                </Typography>
                <Typography color="textSecondary">views</Typography>
              </Box>
              <Typography variant="body2" color="textSecondary">
                {analyticsData.referrers?.referrerSummary?.Instagram?.uniqueViews || 0} unique visitors
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                YoungPresso.com Traffic
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="h4" sx={{ mr: 2 }}>
                  {analyticsData.referrers?.referrerSummary?.['YoungPresso.com']?.totalViews || 0}
                </Typography>
                <Typography color="textSecondary">views</Typography>
              </Box>
              <Typography variant="body2" color="textSecondary">
                {analyticsData.referrers?.referrerSummary?.['YoungPresso.com']?.uniqueViews || 0} unique visitors
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      ) : (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="textSecondary">
            Click on the Analytics tab to load data
          </Typography>
        </Box>
      )}
    </>
  );
};

export default AnalyticsTab;
